import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { billingApi, type BillingStatus } from '@/services/billingApi';
import { ApiClientError } from '@/services/apiClient';

export type BillingLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface BillingState {
  status: BillingLoadStatus;
  data: BillingStatus | null;
  error: string | null;
}

const initialState: BillingState = {
  status: 'idle',
  data: null,
  error: null,
};

export const fetchBilling = createAsyncThunk<BillingStatus, void, { rejectValue: string }>(
  'billing/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await billingApi.getMe();
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Failed to load billing status';
      return rejectWithValue(msg);
    }
  },
);

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearBilling(state) {
      state.status = 'idle';
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBilling.pending, (state) => {
        if (state.status !== 'ready') state.status = 'loading';
      })
      .addCase(fetchBilling.fulfilled, (state, action) => {
        state.status = 'ready';
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchBilling.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Failed to load billing status';
      });
  },
});

export const { clearBilling } = billingSlice.actions;
export default billingSlice.reducer;
